/**
 * Frontend Logger
 * Lightweight logger for the Vite SPA operator dashboard.
 * Provides the same interface as the Next.js logger for compatibility
 * with operator-api client modules.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  operatorId?: string;
  sessionId?: string;
  requestId?: string;
  duration?: number;
  [key: string]: unknown;
}

const SERVICE_NAME = 'sidehustle-web';
const LOG_TO_CONSOLE = import.meta.env.DEV;

const CONSOLE_METHODS: Record<LogLevel, typeof console.log> = {
  debug: console.debug,
  info: console.log,
  warn: console.warn,
  error: console.error,
};

function log(level: LogLevel, message: string, context?: LogContext): void {
  if (LOG_TO_CONSOLE) {
    CONSOLE_METHODS[level](`[${SERVICE_NAME}] ${level.toUpperCase()}: ${message}`, context || '');
  }
}

export const logger = {
  debug: (message: string, context?: LogContext) => log('debug', message, context),
  info: (message: string, context?: LogContext) => log('info', message, context),
  warn: (message: string, context?: LogContext) => log('warn', message, context),
  error: (message: string, context?: LogContext) => log('error', message, context),

  apiCall: (endpoint: string, method: string, duration: number, status: number, context?: LogContext) =>
    log(status >= 400 ? 'error' : 'info', `API ${method} ${endpoint}`, {
      ...context,
      action: 'api_call',
      component: 'api_client',
      duration,
      status,
    }),

  userAction: (action: string, context?: LogContext) =>
    log('info', `User action: ${action}`, { ...context, action: 'user_action', component: 'ui' }),

  operatorAction: (action: string, resourceType: string, resourceId?: string, context?: LogContext) =>
    log('info', `Operator action: ${action}`, {
      ...context,
      action: 'operator_action',
      component: 'operator_dashboard',
      resourceType,
      resourceId,
    }),

  authEvent: (event: string, success: boolean, context?: LogContext) =>
    log(success ? 'info' : 'warn', `Auth event: ${event}`, {
      ...context,
      action: 'auth_event',
      component: 'auth',
      success,
    }),

  performanceMetric: (metric: string, value: number, context?: LogContext) =>
    log('info', `Performance: ${metric}`, {
      ...context,
      action: 'performance',
      component: 'metrics',
      value,
    }),

  pageView: (page: string, context?: LogContext) =>
    log('info', `Page view: ${page}`, { ...context, action: 'page_view', component: 'router' }),

  flush: () => {
    // No-op in Vite SPA - no log batching
  },
};

export default logger;
